import { useState, useEffect } from 'react';
import { cartService } from '@/services/cartService';

/**
 * Hook to manage cart status for a specific content item
 * 
 * @param contentId - The content ID to track (can be number or string)
 * @returns Object with cart status, loading state, and add to cart function
 * 
 * @example
 * const { isInCart, isLoading, addToCart } = useCartStatus(report.content_id);
 * 
 * <button onClick={() => addToCart(4490, 'USD')} disabled={isLoading}>
 *   {isInCart ? 'Added to Cart ✓' : 'Add to Cart'}
 * </button>
 */
export function useCartStatus(contentId: string | number) {
    const [isInCart, setIsInCart] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    // Check if item is in cart
    const checkStatus = () => {
        const inCart = cartService.isInCart(contentId);
        setIsInCart(inCart);
    };

    useEffect(() => {
        // Initial check
        checkStatus();

        // Subscribe to cart changes
        const unsubscribe = cartService.subscribe(checkStatus);

        // Cleanup subscription on unmount
        return unsubscribe;
    }, [contentId]);

    /**
     * Add item to cart with loading state management
     */
    const addToCart = async (unitPrice: number, currency: string = 'USD', qty: number = 1, imageUrl?: string) => {
        if (isLoading) return; // Prevent duplicate calls

        setIsLoading(true);
        try {
            await cartService.addItem(String(contentId), unitPrice, currency, qty, imageUrl);
            // State will auto-update via subscription
        } catch (error) {
            console.error('Failed to add to cart:', error);
            throw error; // Re-throw so caller can handle
        } finally {
            setIsLoading(false);
        }
    };

    return {
        isInCart,
        isLoading,
        addToCart
    };
}
